import { Link, Outlet, useLocation } from 'react-router';
import { LogOut, User as UserIcon, Briefcase } from 'lucide-react';
import {
  UniversalAppLayout,
  UniversalTopNav,
  UniversalSubNav,
  UniversalAppFooter,
  UserMenu,
  ThemeToggle,
} from '@afrilink/ui';
import { siteConfig } from '@afrilink/config';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { isSupportUser } from '@/shared/components/routing/defaultRouteForUser';
import { AnnouncementBanner } from '@/domains/announcements/components/AnnouncementBanner';
import {
  adminPrimaryNav,
  supportNav,
  adminSubNavByPrefix,
} from './AdminNavigation';

const ADMIN_THEME_ACCENT = '#4A7C8C';


export function AdminLayoutWrapper() {
  const { user, signOut } = useAuth();
  const { isSalesStaff, isAdmin } = useStaffRole();
  const { pathname } = useLocation();

  // First match wins — see the ordering note in AdminNavigation.tsx.
  const subNavConfig = adminSubNavByPrefix.find((s) => pathname.startsWith(s.prefix));

  const primaryNav = isSupportUser(user) ? supportNav : adminPrimaryNav;

  // A support agent who ALSO holds a sales staff_profile (e.g. Orapeleng —
  // support auth role + sales_agent) has no "Sales" item in the slim support
  // nav, so add a console switch to the user menu. Full staff already reach
  // /sales via the primary nav link above. Route access itself already exists
  // (SalesRoute admits sales_agent/sales_manager/admin) — this is nav only.
  // Sales left the primary nav in AR-11: it is a cross-app SWITCHER, not a
  // section, and it was already duplicated here with different gating — full
  // staff got the nav item, support staff got this menu entry. One control,
  // one rule. The gate is now simply "can this person use /sales".
  const canSwitchToSales = isSalesStaff || isAdmin;

  const userName = user?.email?.split('@')[0] ?? 'Staff';
  const userEmail = user?.email;

  // Wrap context signOut so logout lands on the marketing site, not the
  // portal sign-in screen. Mirrors ClientLayoutWrapper for consistency.
  async function signOutToMarketing() {
    try {
      await signOut();
    } catch {
      /* fall through */
    }
    window.location.href = siteConfig.url;
  }

  return (
    <UniversalAppLayout
      topNav={
        <UniversalTopNav
          accentColor={ADMIN_THEME_ACCENT}
          brand={
            /* The three `white` literals in this block are DELIBERATE and must
               survive the theme burn-down. UniversalTopNav paints its own
               opaque accent ground (ADMIN_THEME_ACCENT), so this text sits on
               teal in BOTH themes — it is not on `background` or `card`, and a
               token that flips with the theme would make the brand lockup
               vanish in one of them. Same reasoning as the white-on-danger
               badge in AdminDashboard. */
            <Link
              to="/admin"
              className="flex items-center gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              aria-label="Admin dashboard"
            >
              <img
                src="/images/logo.png"
                alt=""
                aria-hidden
                width={32}
                height={32}
                className="h-8 w-auto shrink-0"
                loading="eager"
                decoding="async"
              />
              <span className="hidden flex-col leading-tight sm:flex">
                <span className="font-display text-base font-bold tracking-tight text-white drop-shadow-sm">
                  {siteConfig.name}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/50">
                  Admin Console
                </span>
              </span>
            </Link>
          }
          items={primaryNav}
          right={
            <>
              {/* Present in all three shells (admin / client / sales). It was
                  staff-only while the client app still carried 282 theme-blind
                  classes; that burn-down landed in the same pass, so the gate
                  is gone. Hidden below `sm` — the mobile bar has no room, and
                  the OS preference already covers phones via `system`. */}
              <ThemeToggle onAccent className="mr-1 hidden sm:inline-flex" />
              <UserMenu
              name={userName}
              email={userEmail}
              items={[
                ...(canSwitchToSales
                  ? [{
                      label: 'Switch to Sales Console',
                      icon: Briefcase,
                      onSelect: () => { window.location.href = '/sales'; },
                    }]
                  : []),
                {
                  // Honest UI: there is no admin profile page yet. This was a
                  // live-looking no-op; render it visibly disabled until the
                  // route exists rather than letting it silently do nothing.
                  label: 'Profile settings',
                  icon: UserIcon,
                  disabled: true,
                },
                {
                  label: 'Sign out',
                  icon: LogOut,
                  tone: 'danger',
                  separated: true,
                  onSelect: signOutToMarketing,
                },
              ]}
              />
            </>
          }
        />
      }
      subNav={subNavConfig && <UniversalSubNav items={subNavConfig.items} />}
      footer={
        <UniversalAppFooter
          legalName={siteConfig.legalName}
          registration={siteConfig.registration}
        />
      }
    >
      <AnnouncementBanner audience="staff" />
      <Outlet />
    </UniversalAppLayout>
  );
}
